import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarPlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { baht, formatDate } from '../../utils/format';
import { spring } from '../../utils/motion';
import type { Offer } from '../../types/marketplace';
interface BookingTicketProps {
  offer: Offer;
  tear?: boolean;
}
export function BookingTicket({
  offer,
  tear
}: BookingTicketProps) {
  const reduce = useReducedMotion();
  const {
    matches,
    getEvent,
    getPerformer
  } = useMarketplace();
  const match = matches.find(m => m.id === offer.matchId);
  const event = match ? getEvent(match.eventId) : undefined;
  const performer = match ? getPerformer(match.performerId) : undefined;
  const slot = event?.slots.find(s => s.id === offer.slotId);
  if (!event || !performer) return null;
  const animateTear = tear && !reduce;
  return <motion.div initial={animateTear ? {
    scale: 0.96,
    opacity: 0
  } : false} animate={{
    scale: 1,
    opacity: 1
  }} transition={spring.bouncy} className="flex w-full max-w-[380px]" data-id="element-75" data-mp="9exttr2h5g86">
      <div className="relative min-w-0 flex-1 overflow-hidden rounded-l-md bg-ink text-bg" data-id="element-76" data-mp="3vl2zsuu3pilm">
        <img src={event.photos[0]} alt="" className="h-20 w-full object-cover opacity-90" data-id="element-77" data-mp="4f1m0eir4awa" />
        <div className="p-4" data-id="element-78" data-mp="8fv9j6sa7non">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-accent" data-id="element-79" data-mp="ddn2m44nxfuf">Booking confirmed</p>
          <p className="mt-1 truncate text-lg font-bold leading-tight" data-id="element-80" data-mp="icgfpvg1f4u2">{event.name}</p>
          <p className="mt-1 text-sm opacity-80" data-id="element-81" data-mp="n3g9irtv16sc">
            {formatDate(event.date, 'EEE d MMM')} · {performer.name}
          </p>
          <p className="text-sm opacity-80" data-id="element-82" data-mp="s2swrdon8fvg">
            {slot?.kind} · {offer.setLength} min · arrive {offer.arrival}
          </p>
          <button type="button" onClick={() => toast.success('Added to your calendar')} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-bg/10 px-3 py-1.5 text-[13px] font-bold transition-colors duration-150 hover:bg-bg/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-83" data-mp="w5xxpbt2s9hz">
            <CalendarPlusIcon className="h-4 w-4" data-id="element-84" data-mp="36wfaog5zsvie" /> Add to calendar
          </button>
        </div>
        <span className="absolute -right-3 -top-3 h-6 w-6 rounded-full bg-bg" aria-hidden="true" data-id="element-85" data-mp="32570vj0r384p" />
        <span className="absolute -bottom-3 -right-3 h-6 w-6 rounded-full bg-bg" aria-hidden="true" data-id="element-86" data-mp="3gtjzknr8352w" />
      </div>
      <motion.div initial={animateTear ? {
      rotate: 0,
      x: 0
    } : false} animate={{
      rotate: 6,
      x: 6,
      y: 4
    }} transition={{
      ...spring.bouncy,
      delay: animateTear ? 0.35 : 0
    }} style={{
      transformOrigin: 'top left'
    }} className="flex w-[92px] shrink-0 flex-col items-center justify-center rounded-r-md border-l-2 border-dashed border-bg/40 bg-ink px-2 text-center text-bg" data-id="element-87" data-mp="3bu763k5uizrc">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] opacity-70" data-id="element-88" data-mp="1qjgsfsio4qlu">Fee</p>
        <p className="tabular text-lg font-bold" data-id="element-89" data-mp="6167pvhiysi1">{baht(offer.fee + offer.travel)}</p>
        <span className="mt-2 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-on-accent" data-id="element-90" data-mp="axwmmfhlfi1v">PAID IN</span>
      </motion.div>
    </motion.div>;
}